import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GoalResponseDto } from '../dto/response/goal-response.dto';
import goalService from '../services/goal.service';
import { GoalSaveRequestDto } from '../dto/request/goal-save-request.dto';
import { GoalUpdateRequestDto } from '../dto/request/goal-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  goals: [] as GoalResponseDto[] | null,
  goal: {} as GoalResponseDto | null,
  error: null as string | null,
};

export const fetchGoals = createAsyncThunk('goals/fetchGoals', async () => {
  return await goalService.fetchGoals();
});

export const fetchGoalById = createAsyncThunk(
  'goals/fetchGoalById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await goalService.fetchGoalById(params.id, params.language);
  }
);

export const saveGoal = createAsyncThunk(
  'goals/saveGoal',
  async (params: {
    saveRequest: GoalSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await goalService.saveGoal(params.saveRequest, params.language);
  }
);

export const updateGoal = createAsyncThunk(
  'goals/updateGoal',
  async (params: {
    updateRequest: GoalUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await goalService.updateGoal(params.updateRequest, params.language);
  }
);

export const deleteGoal = createAsyncThunk(
  'goals/deleteGoal',
  async (existingId: string, { rejectWithValue, dispatch }) => {
    try {
      await goalService.deleteGoal(existingId);
      dispatch(fetchGoals());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const goalSlice = createSlice({
  name: 'goalSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGoals.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.goals = action.payload;
      })
      .addCase(fetchGoals.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchGoalById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchGoalById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.goal = action.payload;
      })
      .addCase(fetchGoalById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveGoal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveGoal.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.goals?.push(action.payload);
        showSuccess('Goal added successfully.');
      })
      .addCase(saveGoal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateGoal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateGoal.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.goals) {
          const goalIndex = state.goals.findIndex(
            (goal) => goal.id === action.payload.id
          );
          if (goalIndex !== -1) {
            state.goals[goalIndex] = action.payload;
          }
        }
      })
      .addCase(updateGoal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteGoal.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteGoal.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Goal deleted successfully.');
      })
      .addCase(deleteGoal.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting goal failed. ${action.payload as string}`);
      });
  },
});

export default goalSlice.reducer;
